// src/components/programs/ProgramForm/Step0Sections/DateRangeSection.jsx
import React from "react";
import { Grid, Box, Stack, Chip, Typography, useTheme, Paper, alpha } from "@mui/material";
import DateRangePicker from "../../../inputs/DateRangePicker";
import { Event as EventIcon, CalendarMonth as CalendarIcon, Nightlight as NightIcon } from "@mui/icons-material";

const DateRangeSection = ({
    formData,
    errors,
    isArabic,
    handleDateChange,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Grid container spacing={3}>
            {/* Date Range Picker */}
            <Grid item xs={12}>
                <DateRangePicker
                    startDate={formData.start_date || ''}
                    endDate={formData.end_date || ''}
                    onStartDateChange={(value) => handleDateChange('start_date', value)}
                    onEndDateChange={(value) => handleDateChange('end_date', value)}
                    error={!!errors.start_date || !!errors.end_date}
                    helperText={errors.start_date || errors.end_date}
                    required
                    isArabic={isArabic}
                />
            </Grid>

            {/* Enhanced Duration Summary Card */}
            {formData.start_date && formData.end_date && (
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            background: isDark
                                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`
                                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                            border: `2px solid ${isDark 
                                ? alpha(theme.palette.primary.main, 0.3)
                                : alpha(theme.palette.primary.main, 0.2)
                            }`,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            }
                        }}
                    >
                        <Grid container spacing={3} alignItems="center">
                            {/* Icon */}
                            <Grid item xs={12} sm="auto">
                                <Box
                                    sx={{
                                        width: 70,
                                        height: 70,
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                                    }}
                                >
                                    <EventIcon sx={{ fontSize: 36, color: '#FFFFFF' }} />
                                </Box>
                            </Grid>

                            {/* Duration Info */}
                            <Grid item xs={12} sm>
                                <Typography 
                                    variant="overline" 
                                    color="text.secondary"
                                    fontWeight={700}
                                    letterSpacing={1.5}
                                    display="block"
                                    sx={{ mb: 1 }}
                                >
                                    {isArabic ? 'مدة البرنامج' : 'PROGRAM DURATION'}
                                </Typography>
                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                    <Chip
                                        icon={<CalendarIcon />}
                                        label={`${formData.durations_days || 0} ${isArabic ? 'يوم' : (formData.durations_days === 1 ? 'Day' : 'Days')}`}
                                        color="primary"
                                        sx={{ 
                                            fontWeight: 700,
                                            fontSize: '0.95rem',
                                            height: 36,
                                            px: 1,
                                            '& .MuiChip-icon': {
                                                fontSize: 20,
                                            }
                                        }}
                                    />
                                    {(formData.durations_nights || 0) > 0 && (
                                        <Chip
                                            icon={<NightIcon />}
                                            label={`${formData.durations_nights || 0} ${isArabic ? (formData.durations_nights === 1 ? 'ليلة' : 'ليالي') : (formData.durations_nights === 1 ? 'Night' : 'Nights')}`}
                                            color="secondary"
                                            sx={{ 
                                                fontWeight: 700,
                                                fontSize: '0.95rem',
                                                height: 36,
                                                px: 1,
                                                '& .MuiChip-icon': {
                                                    fontSize: 20,
                                                }
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Grid>

                            {/* Dates Display */}
                            <Grid item xs={12} sm="auto">
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: isDark
                                            ? alpha('#FFFFFF', 0.05)
                                            : alpha('#000000', 0.03),
                                        border: `1px solid ${isDark 
                                            ? alpha('#FFFFFF', 0.1)
                                            : alpha('#000000', 0.06)
                                        }`,
                                        minWidth: 140,
                                    }}
                                >
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography 
                                                variant="caption" 
                                                color="text.secondary" 
                                                fontWeight={600}
                                                display="block"
                                            >
                                                {isArabic ? 'من' : 'FROM'}
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                fontWeight={700} 
                                                color="primary.main"
                                                sx={{ fontSize: '0.95rem' }}
                                            >
                                                {formData.start_date}
                                            </Typography>
                                        </Box>
                                        <Box 
                                            sx={{ 
                                                height: 1, 
                                                bgcolor: 'divider',
                                                my: 0.5,
                                            }} 
                                        />
                                        <Box>
                                            <Typography 
                                                variant="caption" 
                                                color="text.secondary" 
                                                fontWeight={600}
                                                display="block"
                                            >
                                                {isArabic ? 'إلى' : 'TO'}
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                fontWeight={700} 
                                                color="primary.main"
                                                sx={{ fontSize: '0.95rem' }}
                                            >
                                                {formData.end_date}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            )}
        </Grid>
    );
};

export default DateRangeSection;
